import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { userNotesFormFields } from '../../../../forms/fields/formFields/userNotes';
import { userNotesFormSchema } from '../../../../forms/validationSchema/formSchema/userNotes';
import { useAppDispatch } from '../../../../redux/hooks';
import { NotesTypes, UserNotes } from '../../../../types/userNotes';
import { isDispatchApiSuccess } from '../../../../utils/api';
import { createUserNote, updateUserNote } from '../../../../redux/modules/user/user.actions';
import { userNotesSelector } from '../../../../redux/modules/user/user.selectors';
import { fetchStatuses } from '../../../../constants/fetchStatuses';

interface UserNotesDialogProps {
  open: boolean;
  userId: number;
  initialValue: UserNotes | null;
  onClose: (isReload: boolean) => void;
}

export function UserNotesDialog({ userId, open, initialValue, onClose }: UserNotesDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { submitUserNoteStatus, error } = useSelector(userNotesSelector);
  const isLoading = submitUserNoteStatus === fetchStatuses.pending;

  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, resetForm } = useFormik({
    initialValues: {
      [userNotesFormFields.content]: '',
      [userNotesFormFields.type]: '',
      userId,
    },
    validationSchema: userNotesFormSchema,
    onSubmit: async (formValues) => {
      if (initialValue?.id) {
        const res = await dispatch(updateUserNote({ id: initialValue.id, ...formValues }));
        if (isDispatchApiSuccess(res)) {
          onClose(true);
          enqueueSnackbar('User Note updated!', { variant: 'success' });
        } else {
          enqueueSnackbar('User Note update error!', { variant: 'error' });
        }
      } else {
        const res = await dispatch(createUserNote(formValues));

        if (isDispatchApiSuccess(res)) {
          onClose(true);
          enqueueSnackbar('User Note created!', { variant: 'success' });
        } else {
          enqueueSnackbar('User Note creation error!', { variant: 'error' });
        }
      }
    },
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setValues({
      content: initialValue?.content || '',
      type: initialValue?.type || '',
      userId,
    });
  }, [initialValue]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{initialValue?.id ? 'Edit User Note' : 'Create a new User Note'}</DialogTitle>
      <DialogContent>
        <DialogContentText>{error && <Alert severity="error">{error.message}</Alert>}</DialogContentText>
        <form onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="content"
            name={userNotesFormFields.content}
            label="Content"
            value={values[userNotesFormFields.content]}
            onChange={handleChange}
            error={touched.content && !!errors.content}
            helperText={touched.content && errors.content}
            multiline
            rows={5}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="type">Type</InputLabel>
            <Select
              labelId="type"
              label="Type"
              name="type"
              value={values[userNotesFormFields.type]}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="request">{NotesTypes.request}</MenuItem>
              <MenuItem value="event">{NotesTypes.event}</MenuItem>
              <MenuItem value="action">{NotesTypes.action}</MenuItem>
              <MenuItem value="other">{NotesTypes.other}</MenuItem>
            </Select>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={isLoading}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
